<template>
  <div>
    <header
      class="border-b border-slate-200 bg-gradient-to-r from-white via-slate-50/50 to-white backdrop-blur-sm shadow-lg sticky top-0 z-50"
    >
      <div
        class="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between"
      >
        <NuxtLink to="/" class="flex items-center">
          <img
            src="/jg-logo-t.webp"
            alt="Junk Guys MN Logo"
            class="h-20 sm:h-24 w-auto p-2"
          />
        </NuxtLink>

        <!-- Desktop Navigation -->
        <nav
          class="hidden md:flex items-center gap-6 lg:gap-8 text-sm lg:text-base font-medium"
        >
          <NuxtLink
            to="/"
            class="text-slate-700 hover:text-primary-main transition-all duration-200 hover:scale-105 transform relative group"
          >
            Home
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-main transition-all duration-200 group-hover:w-full"
            ></span>
          </NuxtLink>
          <NuxtLink
            to="/about"
            class="text-slate-700 hover:text-primary-main transition-all duration-200 hover:scale-105 transform relative group"
          >
            About
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-main transition-all duration-200 group-hover:w-full"
            ></span>
          </NuxtLink>
          <NuxtLink
            to="/services"
            class="text-slate-700 hover:text-primary-main transition-all duration-200 hover:scale-105 transform relative group"
          >
            Services
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-main transition-all duration-200 group-hover:w-full"
            ></span>
          </NuxtLink>
          <NuxtLink
            to="/pricing"
            class="text-slate-700 hover:text-primary-main transition-all duration-200 hover:scale-105 transform relative group"
          >
            Pricing
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-main transition-all duration-200 group-hover:w-full"
            ></span>
          </NuxtLink>
          <NuxtLink
            to="/faq"
            class="text-slate-700 hover:text-primary-main transition-all duration-200 hover:scale-105 transform relative group"
          >
            FAQ
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-main transition-all duration-200 group-hover:w-full"
            ></span>
          </NuxtLink>
          <NuxtLink
            to="/book"
            class="rounded-xl bg-gradient-to-r from-primary-main to-primary-dark px-4 lg:px-6 py-2.5 lg:py-3 text-white font-semibold hover:from-primary-dark hover:to-primary-dark shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 hover:scale-105 border border-primary-main/20"
          >
            Book Now
          </NuxtLink>
        </nav>

        <!-- Mobile menu button -->
        <button
          @click.stop="toggleMobileMenu"
          class="md:hidden p-2 rounded-lg text-slate-700 hover:text-primary-main hover:bg-slate-100 transition-colors"
          :aria-expanded="isMobileMenuOpen"
          aria-label="Toggle mobile menu"
        >
          <svg
            v-if="!isMobileMenuOpen"
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            ></path>
          </svg>
          <svg
            v-else
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <!-- Mobile Navigation Menu -->
      <div
        v-show="isMobileMenuOpen"
        class="md:hidden border-t border-slate-200 bg-white shadow-lg"
      >
        <nav class="px-4 py-4 space-y-3">
          <NuxtLink
            to="/"
            @click="closeMobileMenu"
            class="block py-3 px-4 text-slate-700 hover:text-primary-main hover:bg-slate-50 rounded-lg transition-colors font-medium"
          >
            Home
          </NuxtLink>
          <NuxtLink
            to="/about"
            @click="closeMobileMenu"
            class="block py-3 px-4 text-slate-700 hover:text-primary-main hover:bg-slate-50 rounded-lg transition-colors font-medium"
          >
            About
          </NuxtLink>
          <NuxtLink
            to="/services"
            @click="closeMobileMenu"
            class="block py-3 px-4 text-slate-700 hover:text-primary-main hover:bg-slate-50 rounded-lg transition-colors font-medium"
          >
            Services
          </NuxtLink>
          <NuxtLink
            to="/pricing"
            @click="closeMobileMenu"
            class="block py-3 px-4 text-slate-700 hover:text-primary-main hover:bg-slate-50 rounded-lg transition-colors font-medium"
          >
            Pricing
          </NuxtLink>
          <NuxtLink
            to="/faq"
            @click="closeMobileMenu"
            class="block py-3 px-4 text-slate-700 hover:text-primary-main hover:bg-slate-50 rounded-lg transition-colors font-medium"
          >
            FAQ
          </NuxtLink>
          <NuxtLink
            to="/book"
            @click="closeMobileMenu"
            class="block py-3 px-4 bg-gradient-to-r from-primary-main to-primary-dark text-white rounded-lg font-semibold hover:from-primary-dark hover:to-primary-dark transition-all duration-200"
          >
            Book Now
          </NuxtLink>
        </nav>
      </div>
    </header>

    <main>
      <slot />
    </main>

    <footer class="border-t">
      <div class="mx-auto max-w-7xl px-4 py-8 text-sm text-slate-600">
        <div
          class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-center md:text-left"
        >
          <div class="flex flex-col md:flex-row items-center gap-3">
            <img
              src="/jg-logo-t.webp"
              alt="Junk Guys MN Logo"
              class="h-16 w-auto"
            />
            <p>
              © {{ new Date().getFullYear() }} Junk Guys MN • Junk & Light Demo
              in Minnesota
            </p>
          </div>
          <p>
            Call:
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="underline"
              >{{ phoneNumberPretty }}</a
            >
          </p>
        </div>
        <div class="mt-4 text-center text-xs text-slate-500">
          <p>
            Developed by
            <a
              href="https://mitbrew.com/"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary-main hover:text-primary-dark underline font-semibold"
            >
              MITBrew
            </a>
          </p>
        </div>
      </div>
    </footer>
    <UiBackToTop />
  </div>
</template>

<script setup>
import { usePhoneNumber } from "../composables/usePhoneNumber";

// Get phone number
const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();

// Mobile menu state
const isMobileMenuOpen = ref(false);

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

// Close mobile menu when clicking outside
onMounted(() => {
  const handleClickOutside = (event) => {
    const header = document.querySelector("header");
    if (header && !header.contains(event.target)) {
      isMobileMenuOpen.value = false;
    }
  };

  document.addEventListener("click", handleClickOutside);

  onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
  });
});

// Close mobile menu on route change
const router = useRouter();
watch(
  () => router.currentRoute.value.path,
  () => {
    isMobileMenuOpen.value = false;
  },
);

// Gtag conversion tracking
function trackConversion() {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", {
      send_to: "AW-17500805163/GT-NGBBLDKN",
    });
  }
}
</script>

<style scoped>
/* Custom header animations */
.group:hover .group-hover\:w-full {
  width: 100%;
}

/* Smooth transitions for all interactive elements */
* {
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke,
    opacity, box-shadow, transform, filter, backdrop-filter;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Enhanced focus states for accessibility */
button:focus-visible,
a:focus-visible {
  outline: 2px solid #6a0dad;
  outline-offset: 2px;
}

/* Mobile menu animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
